import React from 'react';
import I18n from 'i18n-js';
import OffersNudge from '@reverbdotcom/commons/src/offers/offers_nudge';
import OffersCurrencyConversion from '@reverbdotcom/commons/src/offers/offers_currency_conversion';
import {
  core_apimessages_Money,
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_NegotiationAction as NegotiationAction,
  reverb_config_cache_ExchangeRate as ExchangeRate,
  Listing,
  Negotiation,
} from '../../schemas/rql';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroup from '@reverbdotcom/commons/src/components/form_group';
import * as NegotiationGetters from './negotiation_getters';
import { UserInputOffer } from '@reverbdotcom/commons/src/offers/negotiation_types';
import {
  CombinedSaleAndShippingNotice,
} from '@reverbdotcom/commons/src/offers/offer_notices';
import OfferAmountInputs from '@reverbdotcom/commons/src/offers/offer_amount_inputs';
import bind from '@reverbdotcom/commons/src/bind';
import Currency from '@reverbdotcom/commons/src/currency';
import { ControlledBodyInput } from '../messages/body_input';
import { IPhotoAttrs } from '../photos/photo';

export interface IProps {
  error?: string;
  listing: Listing;
  negotiation: Negotiation;
  currency: string;
  onOfferChange: (offer: Partial<UserInputOffer>) => void;
  offer: UserInputOffer;
  party: NegotiationParty;
  action: NegotiationAction;
  exchangeRates: ExchangeRate[];
}

interface IState {
  photos: IPhotoAttrs[];
}

function CounterNotices({ party, listing, negotiation }: IProps) {
  if (party === NegotiationParty.BUYER) {
    return (
      <p className="offers-form__notice offers-form__notice--purchase">
        {I18n.t(
          'discovery.offers.buyerCompletePurchaseNotice',
          { shopName: listing.shop.name },
        )}
      </p>
    );
  }

  return (
    <p className="offers-form__notice offers-form__notice--purchase">
      {I18n.t(
        'discovery.offers.sellerCompletePurchaseNotice',
        { buyerName: negotiation.buyer.name },
      )}
    </p>
  );
}

class UpdateOfferFormFields extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);

    this.state = { photos: [] };
  }

  get lastOffer() {
    return NegotiationGetters.lastOffer(this.props.negotiation);
  }

  get shippingPriceInShopCurrency(): core_apimessages_Money {
    return NegotiationGetters.offerOriginalShippingPriceAmount(this.lastOffer);
  }

  get messageLabel() {
    if (this.props.party === NegotiationParty.SELLER) {
      return I18n.t('discovery.offers.sellerMessageLabel');
    }

    return I18n.t('discovery.offers.buyerMessageLabel');
  }

  renderOfferError() {
    if (!this.props.error) {
      return null;
    }

    return <p className="offers-form__error">{this.props.error}</p>;
  }

  @bind
  handleMessageChange(message: string) {
    this.props.onOfferChange({ message });
  }

  @bind
  handlePhotosChange(photos: IPhotoAttrs[]) {
    this.setState({ photos });
  }

  get labelTaxHint() {
    if (this.props.negotiation.taxIncluded) {
      return ` (${this.props.negotiation.taxIncludedHint})`;
    }

    return '';
  }

  renderCounterFields() {
    if (this.props.action !== NegotiationAction.COUNTER) {
      return null;
    }

    const { offer, listing, party, currency, negotiation, onOfferChange } = this.props;
    const taxHint = negotiation.taxIncluded ? ` (${negotiation.taxIncludedHint})` : '';
    const currencyDescription = Currency.get(currency);

    return [
      <OfferAmountInputs
        key="0"
        offer={offer}
        listing={listing}
        party={party}
        action={NegotiationAction.COUNTER}
        currency={currencyDescription.displayCode}
        taxable={negotiation}
        onOfferChange={onOfferChange}
        autoFocus
      />,
      (party === NegotiationParty.BUYER &&
        <OffersNudge
          key="1"
          offerAmount={offer.price}
          originalAmount={listing.price.amount}
          currency={currency}
        />
      ),
      (party === NegotiationParty.SELLER &&
        <FormGroupWithInput
          key="2"
          label={`${I18n.t('discovery.offers.label.counterOfferShippingPrice')}${taxHint}`}
          fieldName="shippingPrice"
          inputName="offer-shipping-price"
          currencyPrefix={currencyDescription.symbol}
          value={offer.shippingPrice}
          updateField={onOfferChange}
          disabled={false}
          numberOnly
          required
        />),
    ];
  }

  render() {
    return (
      <div className="offers-form__body">
        {this.renderOfferError()}

        {this.renderCounterFields()}

        <OffersCurrencyConversion
          offerAmount={this.props.offer.price}
          shippingPrice={this.shippingPriceInShopCurrency}
          rates={this.props.exchangeRates}
          userCurrency={this.props.currency}
          shopCurrency={this.props.listing.shop.currency}
        />

        <FormGroup label={this.messageLabel} tagOptional>
          <ControlledBodyInput
            message={this.props.offer.message}
            onChange={this.handleMessageChange}
            photos={this.state.photos}
            onPhotosChange={this.handlePhotosChange}
            inputNamespace=""
            bodyInputName="message"
            productId={this.props.listing.id}
            isSeller={this.props.party === NegotiationParty.SELLER}
            allowPhotos={false}
            isRequired={false}
          />
        </FormGroup>

        <div className="offers-form__notices">
          <CombinedSaleAndShippingNotice listing={this.props.listing} />
          {this.props.action === NegotiationAction.COUNTER &&
            <CounterNotices {...this.props} />
          }
        </div>
      </div>
    );
  }
}

export default UpdateOfferFormFields;
